import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, CreditCard, Shield, Clock, CheckCircle, 
  AlertCircle, Info, Calculator, Loader2,
  Wallet, ArrowDownRight, X, RefreshCw, CheckCheck
} from 'lucide-react';
import Loading from '../../components/common/Loading';
import { useAuth } from '../../context/AuthContext';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { AlertModal } from '../../components/common/Modal'; // Import AlertModal
import { initializePayment, verifyPaymentStatus, getWalletBalance, getFeePreview } from '../../services/api';

const DepositPage = () => {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState('');
  const [displayAmount, setDisplayAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [paymentReference, setPaymentReference] = useState(null);
  const [feeBreakdown, setFeeBreakdown] = useState(null);
  const [showFeeDetails, setShowFeeDetails] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle, processing, pending_verification, success, failed
  const [calculatingFees, setCalculatingFees] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);

  // Refs for focus management
  const mobileAmountInputRef = useRef(null);
  const desktopAmountInputRef = useRef(null);

  useEffect(() => {
    if (!authUser) {
      navigate('/login');
      return;
    }
    
    // Check for existing pending payment
    const pendingPayment = localStorage.getItem('pending_payment');
    if (pendingPayment) {
      const paymentData = JSON.parse(pendingPayment);
      setPaymentReference(paymentData.reference);
      setPaymentStatus('pending_verification');
      setPaymentInfo({
        amount: paymentData.amount,
        timestamp: paymentData.timestamp
      });
    }

    return () => {
      // Cleanup any existing polling
      if (window.paymentPollingInterval) {
        clearInterval(window.paymentPollingInterval);
      }
    };
  }, [authUser, navigate]);

  const formatNumberWithCommas = (value) => {
    if (!value) return '';
    
    const numericValue = value.replace(/[^0-9.]/g, '');
    if (!numericValue) return '';
    
    const parts = numericValue.split('.');
    let wholePart = parts[0];
    let decimalPart = parts[1] || '';
    
    if (decimalPart.length > 2) {
      decimalPart = decimalPart.substring(0, 2);
    }
    
    if (wholePart) {
      wholePart = wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    
    return decimalPart ? `${wholePart}.${decimalPart}` : wholePart;
  };

  const handleAmountChange = (e, isMobile = false) => {
    const input = e.target.value;
    let cleaned = input.replace(/[₦,]/g, '');
    cleaned = cleaned.replace(/[^0-9.]/g, '');
    
    const decimalCount = (cleaned.match(/\./g) || []).length;
    if (decimalCount > 1) {
      const parts = cleaned.split('.');
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    
    setAmount(cleaned);
    
    if (cleaned) {
      const formatted = formatNumberWithCommas(cleaned);
      setDisplayAmount(`₦${formatted}`);
    } else {
      setDisplayAmount('');
    }
    
    setError('');
    setSuccess('');
    setFeeBreakdown(null);
    setShowFeeDetails(false);

    // Keep focus on input with setTimeout
    setTimeout(() => {
      if (isMobile) {
        mobileAmountInputRef.current?.focus();
      } else {
        desktopAmountInputRef.current?.focus();
      }
    }, 10);
  };

  const handleQuickAmount = async (quickAmount, isMobile = false) => {
    setAmount(quickAmount.toString());
    const formatted = formatNumberWithCommas(quickAmount.toString());
    setDisplayAmount(`₦${formatted}`);
    setError('');
    setSuccess('');
    
    // Focus back on amount input
    setTimeout(() => {
      if (isMobile) {
        mobileAmountInputRef.current?.focus();
      } else {
        desktopAmountInputRef.current?.focus();
      }
    }, 10);
    
    // Calculate fees for quick amount
    if (quickAmount >= 100) {
      await calculateFeesForAmount(quickAmount);
    } else {
      setFeeBreakdown(null);
      setShowFeeDetails(false);
    }
  };

  const resetInputs = () => {
    setAmount('');
    setDisplayAmount('');
    setFeeBreakdown(null);
    setShowFeeDetails(false);
    setPaymentStatus('idle');
    setPaymentReference(null);
    setPaymentInfo(null);
    localStorage.removeItem('pending_payment');
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₦0.00';
    
    return `₦${parseFloat(amount).toLocaleString('en-NG', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  const calculateFeesForAmount = useCallback(async (depositAmount) => {
    if (!depositAmount || depositAmount < 100) {
      setFeeBreakdown(null);
      setShowFeeDetails(false);
      return null;
    }
    
    try {
      setCalculatingFees(true);
      setError('');
      
      // Call backend API to get actual fee breakdown
      const feeData = await getFeePreview(depositAmount, 'deposit');
      
      if (feeData.success) {
        setFeeBreakdown(feeData.fee_info);
        setShowFeeDetails(true);
        return feeData.fee_info;
      } else {
        throw new Error(feeData.error || 'Failed to calculate fees');
      }
      
    } catch (error) {
      // console.error('Error calculating fees:', error);
      setError('Unable to calculate fees. Please try again.');
      setFeeBreakdown(null);
      return null;
    } finally {
      setCalculatingFees(false);
    }
  }, []);

  // Debounced fee calculation
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (amount && parseFloat(amount) >= 100) {
        calculateFeesForAmount(parseFloat(amount));
      } else {
        setFeeBreakdown(null);
        setShowFeeDetails(false);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [amount, calculateFeesForAmount]);

  const handleDeposit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
  
    const depositAmount = parseFloat(amount);
    
    if (depositAmount < 100) {
      setError('Minimum deposit amount is ₦100');
      return;
    }
  
    try {
      setProcessing(true);
      setError('');
      setSuccess('');
  
      // Ensure we have fresh fee calculation
      const latestFees = await calculateFeesForAmount(depositAmount);
      if (!latestFees) {
        setError('Unable to calculate fees. Please try again.');
        setProcessing(false);
        return;
      }
  
      const netAmount = latestFees.net_amount || latestFees.user_gets || depositAmount;
      const totalFees = latestFees.total_fees || 0;
      
      const paymentResult = await initializePayment(
        authUser.user_id,
        authUser.email,
        depositAmount,
        'Wallet Deposit'
      );
  
      if (paymentResult.success) {
        // Use the fee breakdown from our calculation
        if (latestFees) {
          setFeeBreakdown(latestFees);
        }
        setShowFeeDetails(true);
  
        const paymentRef = paymentResult.reference;
        const authorizationUrl = paymentResult.authorization_url;
        setPaymentReference(paymentRef);
        
        // Store payment info for verification
        const paymentData = {
          reference: paymentRef,
          authorization_url: authorizationUrl,
          amount: depositAmount,
          feeBreakdown: latestFees,
          timestamp: Date.now()
        };
        
        localStorage.setItem('pending_payment', JSON.stringify(paymentData));
        
        setPaymentInfo({
          amount: depositAmount,
          reference: paymentRef,
          authorization_url: authorizationUrl,
          timestamp: Date.now()
        });
  
        // Show payment modal with instructions
        setShowPaymentModal(true);
        
      } else {
        throw new Error(paymentResult.error || 'Payment initialization failed');
      }
    } catch (err) {
      // console.error('Payment error:', err);
      setError('Payment failed: ' + err.message);
      setPaymentStatus('failed');
    } finally {
      setProcessing(false);
    }
  };
  

  const handleVerifyPayment = async () => {
    if (!paymentReference) {
      setError('No payment reference found');
      return;
    }
  
    try {
      setProcessing(true);
      setError('');
      // console.log('🔄 Starting verification for reference:', paymentReference);
      
      const statusResult = await verifyPaymentStatus(paymentReference, authUser.user_id);
      // console.log('📥 API Response:', statusResult);
      // console.log('📥 Response success:', statusResult.success);
      // console.log('📥 Response status:', statusResult.status);
      // console.log('📥 Response error:', statusResult.error);
      
      if (statusResult.success === false) {
        // console.log('❌ Payment failed/abandoned');
        const paystackStatus = statusResult.status;
        
        if (paystackStatus === 'abandoned') {
          // console.log('❌ Payment abandoned');
          setPaymentStatus('failed');
          setError('Payment was not completed. Please try the payment again.');
          localStorage.removeItem('pending_payment');
          resetInputs();
        } else if (paystackStatus === 'failed') {
          // console.log('❌ Payment failed');
          setPaymentStatus('failed');
          setError(statusResult.error || 'Payment failed. Please try again.');
          localStorage.removeItem('pending_payment');
          resetInputs();
        } else {
          // console.log('⏳ Payment still processing:', paystackStatus);
          setPaymentStatus('pending_verification');
          setError(statusResult.error || 'Payment is still processing. Please try again later.');
        }
        return;
      }
      
      // console.log('✅ Payment verification successful');
      
      if (statusResult.status === 'success') {
        // console.log('💰 Payment successful, crediting wallet...');
        localStorage.removeItem('pending_payment');
        setPaymentStatus('success');
        
        const depositAmount = paymentInfo?.amount || parseFloat(amount);
        // console.log('💰 Amount to credit:', depositAmount);
        
        try {
          await getWalletBalance(authUser.user_id);
          // console.log('✅ Wallet balance refreshed');
        } catch (walletError) {
          // console.error('⚠️ Error refreshing wallet:', walletError);
          // Don't throw, just log
        }
        
        setShowSuccessModal(true);
        // console.log('✅ Success modal shown');
      } else {
        // console.log('❓ Unexpected status:', statusResult.status);
        setPaymentStatus('pending_verification');
        setError('Payment is still processing. Please try again later.');
      }
      
    } catch (err) {
      // console.error('💥 CATCH BLOCK ERROR:', err);
      // console.error('💥 Error message:', err.message);
      // console.error('💥 Error stack:', err.stack);
      
      setError('Unable to verify payment. Please try again.');
      setPaymentStatus('failed');
      localStorage.removeItem('pending_payment');
    } finally {
      // console.log('🏁 Verification process finished');
      setProcessing(false);
    }
  };

  const handleBack = () => {
    navigate('/wallet');
  };

  const handleRefreshFees = async () => {
    if (amount && parseFloat(amount) >= 100) {
      await calculateFeesForAmount(parseFloat(amount));
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    resetInputs();
    navigate('/wallet');
  };

  const handlePaymentModalClose = () => {
    setShowPaymentModal(false);
    setPaymentStatus('pending_verification');
  };

  const openPaystackWindow = () => {
    if (paymentInfo?.authorization_url) {
      window.open(paymentInfo.authorization_url, '_blank', 'noopener,noreferrer');
    } else if (paymentReference) {
      // Fallback if authorization_url is not available
      const pendingPayment = localStorage.getItem('pending_payment');
      if (pendingPayment) {
        const paymentData = JSON.parse(pendingPayment);
        if (paymentData.authorization_url) {
          window.open(paymentData.authorization_url, '_blank', 'noopener,noreferrer');
          return;
        }
      }
      // Last resort fallback
      const paymentUrl = `https://paystack.com/pay/${paymentReference}`;
      window.open(paymentUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  // ========== MOBILE VIEW ==========
  const MobileView = () => (
    <div className="lg:hidden min-h-screen bg-gray-50 pb-20">
      {/* Success Modal */}
      <AlertModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        title="Deposit Successful!"
        message={`Your deposit of ${formatCurrency(paymentInfo?.amount || amount)} has been successfully added to your wallet.\n\nYou can now use your funds for transactions.`}
        buttonText="Back to Wallet"
        type="success"
        onButtonClick={handleSuccessModalClose}
      />
      
      {/* Payment Instructions Modal */}
      <AlertModal
        isOpen={showPaymentModal}
        onClose={handlePaymentModalClose}
        title="Complete Payment"
        message={`Please complete your payment of ${formatCurrency(paymentInfo?.amount || amount)} on Paystack.\n\nAfter successful payment, return to this page and click "Verify Payment" to confirm.`}
        buttonText="Open Paystack"
        type="info"
        onButtonClick={openPaystackWindow}
      />
      
      {/* Mobile Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5 text-black" />
            </button>
            
            <h1 className="text-lg font-bold text-black">Deposit Funds</h1>
            
            <div className="w-10"></div>
          </div>
        </div>
      </header>

      <main className="px-4 py-4">
        {/* Pending Payment Section */}
        {paymentStatus === 'pending_verification' && paymentInfo && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="w-5 h-5 text-blue-600" />
              <h3 className="font-bold text-black">Payment Pending Verification</h3>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Amount:</span>
                <span className="font-bold">{formatCurrency(paymentInfo.amount)}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Reference:</span>
                <span className="text-sm font-mono">{paymentInfo.reference?.slice(0, 8)}...</span>
              </div>
              
              <div className="pt-3 border-t border-blue-300">
                <p className="text-sm text-gray-600 mb-3">
                  After completing payment on Paystack, click below to verify:
                </p>
                
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleVerifyPayment}
                    disabled={processing}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 rounded-lg font-bold disabled:opacity-50"
                  >
                    {processing ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Verifying...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <CheckCheck className="w-4 h-4" />
                        Verify Payment
                      </div>
                    )}
                  </button>
                  
                  <button
                    onClick={openPaystackWindow}
                    className="w-full border border-[#ff660e] text-[#ff660e] py-3 rounded-lg font-bold hover:bg-orange-50"
                  >
                    Open Paystack Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Amount Input Card */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-gradient-to-br from-[#ff660e] to-orange-500 rounded-lg">
              <ArrowDownRight className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-black">Deposit Amount</h2>
              <p className="text-gray-600 text-sm">Enter amount to deposit</p>
            </div>
          </div>

          {/* Amount Input */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Amount in Naira (₦)
              </label>
              {calculatingFees && (
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Calculating fees...
                </div>
              )}
            </div>
            <div className="relative">
              <input
                ref={mobileAmountInputRef}
                type="text"
                value={displayAmount}
                onChange={(e) => handleAmountChange(e, true)}
                placeholder="₦0.00"
                className="w-full p-4 pl-12 text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-[#ff660e] focus:outline-none focus:ring-2 focus:ring-orange-200 transition-colors"
                disabled={processing || paymentStatus === 'pending_verification'}
              />
              <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-2xl font-bold text-gray-700">
                ₦
              </span>
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </p>
            )}
          </div>

          {/* Quick Amounts */}
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">Quick Select:</p>
            <div className="grid grid-cols-4 gap-2">
              {[1000, 5000, 10000, 20000].map((quickAmount) => (
                <button
                  key={quickAmount}
                  onClick={() => handleQuickAmount(quickAmount, true)}
                  className="p-2 border rounded-lg text-sm hover:border-[#ff660e] hover:bg-orange-50 transition-colors"
                  disabled={processing || calculatingFees || paymentStatus === 'pending_verification'}
                >
                  ₦{quickAmount.toLocaleString()}
                </button>
              ))}
            </div>
          </div>

          {/* Fee Breakdown */}
          {feeBreakdown && showFeeDetails && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-[#ff660e]" />
                  <p className="font-medium text-sm">Fee Breakdown</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleRefreshFees}
                    disabled={calculatingFees}
                    className="p-1 hover:bg-gray-100 rounded"
                    title="Refresh fees"
                  >
                    <RefreshCw className={`w-3 h-3 ${calculatingFees ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={() => setShowFeeDetails(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Deposit:</span>
                  <span>{formatCurrency(feeBreakdown.gross_amount || feeBreakdown.deposit_amount)}</span>
                </div>
                {feeBreakdown.paystack_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Paystack Fee:</span>
                    <span className="text-red-600">-{formatCurrency(feeBreakdown.paystack_fee)}</span>
                  </div>
                )}
                {feeBreakdown.platform_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Platform Fee:</span>
                    <span className="text-red-600">-{formatCurrency(feeBreakdown.platform_fee)}</span>
                  </div>
                )}
                <div className="border-t pt-1">
                  <div className="flex justify-between font-semibold">
                    <span className="text-green-700">To wallet:</span>
                    <span className="text-green-700">{formatCurrency(feeBreakdown.net_amount || feeBreakdown.amount_to_wallet || feeBreakdown.user_gets)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Deposit Button */}
          <button
            onClick={handleDeposit}
            disabled={!amount || parseFloat(amount) < 100 || processing || calculatingFees || paymentStatus === 'pending_verification'}
            className="w-full bg-gradient-to-r from-[#ff660e] to-orange-500 text-white py-3.5 rounded-lg font-bold disabled:opacity-50 hover:from-[#ff5500] hover:to-orange-600 transition-colors"
          >
            {processing ? 'Processing...' : calculatingFees ? 'Calculating...' : 'Continue to Payment'}
          </button>
        </div>

        {/* Info Card */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-5 h-5 text-[#ff660e]" />
            <h3 className="font-bold text-black">How it Works</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold">1</span>
              </div>
              <p className="text-sm text-gray-600">Enter deposit amount (min ₦100)</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold">2</span>
              </div>
              <p className="text-sm text-gray-600">Review real-time fee breakdown</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold">3</span>
              </div>
              <p className="text-sm text-gray-600">Redirect to Paystack for payment</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold">4</span>
              </div>
              <p className="text-sm text-gray-600">Click "Verify Payment" after paying</p>
            </div>
          </div>
        </div>

        {/* Security Card */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-5 h-5 text-green-600" />
            <h3 className="font-bold text-black">Secure Payment</h3>
          </div>
          <p className="text-sm text-gray-600">
            Your payment is processed securely by Paystack. We never store your card details.
          </p>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );

  // ========== DESKTOP VIEW ==========
  const DesktopView = () => (
    <div className="hidden lg:block min-h-screen bg-gray-50">
      {/* Success Modal */}
      <AlertModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        title="Deposit Successful!"
        message={`Your deposit of ${formatCurrency(paymentInfo?.amount || amount)} has been successfully added to your wallet.\n\nYou can now use your funds for transactions.`}
        buttonText="Back to Wallet"
        type="success"
        onButtonClick={handleSuccessModalClose}
      />
      
      {/* Payment Instructions Modal */}
      <AlertModal
        isOpen={showPaymentModal}
        onClose={handlePaymentModalClose}
        title="Complete Payment"
        message={`Please complete your payment of ${formatCurrency(paymentInfo?.amount || amount)} on Paystack.\n\nAfter successful payment, return to this page and click "Verify Payment" to confirm.`}
        buttonText="Open Paystack"
        type="info"
        onButtonClick={openPaystackWindow}
      />
      
      {/* Desktop Header */}
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Deposit Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              {/* Header */}
              <div className="flex items-center gap-4 mb-6">
                <button
                  onClick={handleBack}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-black" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-black">Deposit Funds</h1>
                  <p className="text-gray-600">Add money to your wallet securely</p>
                </div>
              </div>

              {/* Pending Payment Section */}
              {paymentStatus === 'pending_verification' && paymentInfo && (
                <div className="mb-6 p-5 bg-blue-50 border border-blue-200 rounded-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="w-6 h-6 text-blue-600" />
                    <h3 className="text-xl font-bold text-black">Payment Pending Verification</h3>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Amount</p>
                        <p className="text-2xl font-bold">{formatCurrency(paymentInfo.amount)}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Reference</p>
                        <p className="font-mono text-sm">{paymentInfo.reference}</p>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-white rounded-lg border">
                      <p className="text-sm text-gray-600 mb-3">
                        <span className="font-medium">Instructions:</span> After completing payment on Paystack, click the button below to verify and add funds to your wallet.
                      </p>
                      
                      <div className="flex gap-3">
                        <button
                          onClick={handleVerifyPayment}
                          disabled={processing}
                          className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 rounded-lg font-bold disabled:opacity-50 hover:from-green-600 hover:to-emerald-700 transition-colors"
                        >
                          {processing ? (
                            <div className="flex items-center justify-center gap-2">
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Verifying...
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              <CheckCheck className="w-5 h-5" />
                              Verify Payment
                            </div>
                          )}
                        </button>
                        
                        <button
                          onClick={openPaystackWindow}
                          className="flex-1 border border-[#ff660e] text-[#ff660e] py-3 rounded-lg font-bold hover:bg-orange-50 transition-colors"
                        >
                          Open Paystack Again
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Amount Input */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Amount in Naira (₦)
                  </label>
                  {calculatingFees && (
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Calculating fees...
                    </div>
                  )}
                </div>
                <div className="relative">
                  <input
                    ref={desktopAmountInputRef}
                    type="text"
                    value={displayAmount}
                    onChange={handleAmountChange}
                    placeholder="₦0.00"
                    className="w-full p-5 pl-14 text-3xl font-bold border-2 border-gray-300 rounded-xl focus:border-[#ff660e] focus:outline-none focus:ring-2 focus:ring-orange-200 transition-colors"
                    disabled={processing || calculatingFees || paymentStatus === 'pending_verification'}
                  />
                  <span className="absolute left-6 top-1/2 transform -translate-y-1/2 text-3xl font-bold text-gray-700">
                    ₦
                  </span>
                </div>
                {error && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                  </p>
                )}
              </div>

              {/* Quick Amounts */}
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-3">Quick Select:</p>
                <div className="grid grid-cols-4 gap-3">
                  {[1000, 5000, 10000, 20000, 50000, 100000, 200000, 500000].map((quickAmount) => (
                    <button
                      key={quickAmount}
                      onClick={() => handleQuickAmount(quickAmount)}
                      className="p-3 border rounded-xl hover:border-[#ff660e] hover:bg-orange-50 transition-colors"
                      disabled={processing || calculatingFees || paymentStatus === 'pending_verification'}
                    >
                      <span className="font-medium">₦{quickAmount.toLocaleString()}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Fee Breakdown */}
              {feeBreakdown && showFeeDetails && (
                <div className="mb-6 bg-gray-50 border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Calculator className="w-5 h-5 text-[#ff660e]" />
                      <h4 className="font-semibold text-black">Fee Breakdown</h4>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleRefreshFees}
                        disabled={calculatingFees}
                        className="p-1 hover:bg-gray-200 rounded"
                        title="Refresh fees"
                      >
                        <RefreshCw className={`w-4 h-4 ${calculatingFees ? 'animate-spin' : ''}`} />
                      </button>
                      <button
                        onClick={() => setShowFeeDetails(false)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Deposit Amount:</span>
                      <span className="font-medium">{formatCurrency(feeBreakdown.gross_amount || feeBreakdown.deposit_amount)}</span>
                    </div>
                    
                    {feeBreakdown.paystack_fee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Paystack Fee:</span>
                        <span className="font-medium text-red-600">
                          -{formatCurrency(feeBreakdown.paystack_fee)}
                        </span>
                      </div>
                    )}
                    
                    {feeBreakdown.platform_fee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Platform Fee:</span>
                        <span className="font-medium text-red-600">
                          -{formatCurrency(feeBreakdown.platform_fee)}
                        </span>
                      </div>
                    )}
                    
                    <div className="border-t border-gray-300 pt-2">
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-700">Total Fees:</span>
                        <span className="font-semibold text-red-600">
                          -{formatCurrency(feeBreakdown.total_fees)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between pt-2 border-t border-gray-300">
                      <span className="font-bold text-green-700">Amount to Wallet:</span>
                      <span className="font-bold text-green-700 text-lg">
                        {formatCurrency(feeBreakdown.net_amount || feeBreakdown.amount_to_wallet || feeBreakdown.user_gets)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Deposit Button */}
              <button
                onClick={handleDeposit}
                disabled={!amount || parseFloat(amount) < 100 || processing || calculatingFees || paymentStatus === 'pending_verification'}
                className="w-full bg-gradient-to-r from-[#ff660e] to-orange-500 text-white py-4 rounded-xl font-bold text-lg hover:from-[#ff5500] hover:to-orange-600 disabled:opacity-50 transition-colors"
              >
                {processing ? 'Processing...' : calculatingFees ? 'Calculating fees...' : 'Continue to Payment'}
              </button>

              {/* Security Note */}
              <div className="mt-6 pt-6 border-t">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-green-600" />
                  <p className="text-gray-600">
                    <span className="font-medium">Secure Payment:</span> Processed by Paystack with bank-level security.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Info */}
          <div className="space-y-6">
            {/* How it Works */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Info className="w-5 h-5 text-[#ff660e]" />
                <h3 className="font-bold text-black">How it Works</h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-blue-600">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-black">Enter Amount</p>
                    <p className="text-sm text-gray-600">Minimum deposit ₦100</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-blue-600">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-black">Review Fees</p>
                    <p className="text-sm text-gray-600">See real-time fee breakdown</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-blue-600">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-black">Pay with Paystack</p>
                    <p className="text-sm text-gray-600">Card, bank transfer, or USSD</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-blue-600">4</span>
                  </div>
                  <div>
                    <p className="font-medium text-black">Verify Payment</p>
                    <p className="text-sm text-gray-600">Click "Verify Payment" after paying</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Fee Information */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Calculator className="w-5 h-5 text-[#ff660e]" />
                <h3 className="font-bold text-black">Fee Information</h3>
              </div>
              
              <div className="space-y-3">
                <div>
                  <p className="font-medium text-black">Dynamic Fees</p>
                  <p className="text-sm text-gray-600">Calculated based on amount</p>
                </div>
                
                <div>
                  <p className="font-medium text-black">Real-time Calculation</p>
                  <p className="text-sm text-gray-600">See fees update as you type</p>
                </div>
                
                <div>
                  <p className="font-medium text-black">Processing Time</p>
                  <p className="text-sm text-gray-600">Instant after verification</p>
                </div>
              </div>
            </div>

            {/* Support */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h4 className="font-bold text-black mb-3">Need Help?</h4>
              <p className="text-gray-600 mb-4">Having issues with your deposit?</p>
              <button
                onClick={() => navigate('/support')}
                className="text-[#ff660e] hover:text-[#ff5500] font-medium underline"
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </main>
      
      {/* Desktop Footer */}
      <Footer />
    </div>
  );

  return (
    <>
      {/* Render Mobile or Desktop View */}
      <MobileView />
      <DesktopView />
    </>
  );
};

export default DepositPage;